import type { SpacesRequest } from '@/api/data-contracts';
import { resetPastDateIfNoPermission } from '@/shared/lib/filters';
import { FilterValues } from '@/shared/ui/GeneratikTable';

export const filterValuesToSpacesRequest = (
  filterValues: FilterValues,
  userType?: 'backoffice' | 'location' | 'advertiser' | 'service',
): Partial<SpacesRequest> => {
  const request: Partial<SpacesRequest> = {};

  const dateRange = filterValues.dateRange as { start?: string; end?: string } | undefined;
  if (dateRange?.start) {
    request.start = dateRange.start.includes('T')
      ? dateRange.start
      : `${dateRange.start}T00:00:00Z`;
  }
  if (dateRange?.end) {
    request.end = dateRange.end.includes('T') ? dateRange.end : `${dateRange.end}T20:59:59Z`;
  }

  const priceRange = filterValues.priceRange as string | undefined;
  if (priceRange) {
    const [min, max] = priceRange.split('-');
    if (min) request.minPrice = Number(min);
    if (max) request.maxPrice = Number(max);
  }

  const monthlyBudget = filterValues.monthlyBudget as { min?: number; max?: number } | undefined;
  if (monthlyBudget?.min !== undefined) request.monthlyBudgetFrom = monthlyBudget.min;
  if (monthlyBudget?.max !== undefined) request.monthlyBudgetTo = monthlyBudget.max;

  const status = filterValues.status as string[] | undefined;
  if (status?.length) {
    request.status = status as SpacesRequest['status'];
  }

  const published = filterValues.published as string | undefined;

  if (userType === 'location' && published !== 'false') {
    request.published = true;
  } else if (published?.length) {
    request.published = published === 'true';
  }

  const locationType = filterValues.locationType as string[] | undefined;
  if (locationType?.length) {
    request.locationType = locationType as SpacesRequest['locationType'];
  }

  const city = filterValues.city as string[] | undefined;
  if (city?.length) {
    request.city = city;
  }

  const location = filterValues.location as string[] | undefined;
  if (location?.length) {
    request.location = location;
  }

  const supportType = filterValues.supportType as string[] | undefined;
  if (supportType?.length) {
    request.supportType = supportType as SpacesRequest['supportType'];
  }

  const exposureType = filterValues.exposureType as string[] | undefined;
  if (exposureType?.length) {
    request.exposureType = exposureType as SpacesRequest['exposureType'];
  }

  const supportName = filterValues.supportName as string[] | undefined;
  if (supportName?.length) {
    request.supportName = supportName as SpacesRequest['supportName'];
  }

  const placementArea = filterValues.placementArea as string[] | undefined;
  if (placementArea?.length) {
    request.placementArea = placementArea as SpacesRequest['placementArea'];
  }

  const hasLayoutSketches = filterValues.hasLayoutSketches as string | undefined;
  if (hasLayoutSketches?.length) {
    request.hasLayoutSketches = hasLayoutSketches === 'true';
  }

  return request;
};

export function buildInitialFilterValues(
  defaults: Record<string, unknown>,
  hasPermissionToCreatePastBookings = true,
): FilterValues {
  const values: FilterValues = {};

  if (defaults.start || defaults.end || defaults.dateRange) {
    values.dateRange = (defaults.dateRange as FilterValues['dateRange']) ?? {
      start: defaults.start as string | undefined,
      end: defaults.end as string | undefined,
    };
  }

  if (defaults.minPrice || defaults.maxPrice || defaults.priceRange) {
    const min = defaults.minPrice as number | undefined;
    const max = defaults.maxPrice as number | undefined;
    if (min !== undefined && max !== undefined) {
      values.priceRange = `${min}-${max}`;
    } else if (defaults.priceRange) {
      values.priceRange = defaults.priceRange as string;
    }
  }

  if (defaults.monthlyBudgetFrom !== undefined || defaults.monthlyBudgetTo !== undefined) {
    values.monthlyBudget = {
      min: defaults.monthlyBudgetFrom as number | undefined,
      max: defaults.monthlyBudgetTo as number | undefined,
    };
  } else if (defaults.monthlyBudget) {
    values.monthlyBudget = defaults.monthlyBudget as FilterValues['monthlyBudget'];
  }

  if (defaults.status) {
    values.status = defaults.status as string[];
  }

  if (defaults.locationType) {
    values.locationType = defaults.locationType as string[];
  }

  if (defaults.city) {
    values.city = defaults.city as string[];
  }

  if (defaults.location) {
    values.location = defaults.location as string[];
  }

  if (defaults.supportType) {
    values.supportType = defaults.supportType as string[];
  }

  if (defaults.exposureType) {
    values.exposureType = defaults.exposureType as string[];
  }

  if (defaults.supportName) {
    values.supportName = defaults.supportName as string[];
  }

  if (defaults.placementArea) {
    values.placementArea = defaults.placementArea as string[];
  }

  if (defaults.published) {
    values.published = defaults.published as string;
  }

  if (typeof defaults.hasLayoutSketches === 'boolean') {
    values.hasLayoutSketches = String(defaults.hasLayoutSketches);
  }

  return resetPastDateIfNoPermission(values, hasPermissionToCreatePastBookings);
}
